/**
 * IXD 셀 편집 핸들러 훅.
 *
 * Enter 진입 / 붙여넣기 / 삭제 등 편집 진입·벌크 갱신 동작을 모은다.
 * 실제 선택/키보드 바인딩은 useIxdCellSelection 이 담당하고, 이 훅은 그 콜백 구현만 제공한다.
 *
 * batch entry는 셀이 속한 chunk 컨테이너의 data-ixd-fs-type 으로 fsType을 함께
 * 담아 호출자에 전달한다. 양쪽 fsType에 걸친 한 paste/delete도 한 undo 그룹으로
 * 호출자가 처리할 수 있다(소비처가 mixed batch를 지원할 때).
 */

import { useCallback } from "react";
import { ATTR } from "@/utils/dom-attrs";
import { toast } from "sonner";
import type { FsType } from "@/apis/ixd-editor/types";
import type {
  TableCellPasteEntry,
  TableCellPasteResult,
  TableCellSelectionEntry,
} from "@/hooks/use-table-cell-selection";
import {
  getIxdSchemaValueKind,
  normalizeEditedCellValue,
  validateStoredCellValue,
} from "@/utils/ixd-editor/value-validation";
import { exactCellIdSelector } from "@/utils/ixd-dsd-compare/cell-id-selector";
import { isNumericKind } from "@/utils/value-conversion";

export interface CellEditBatchEntry {
  fsType: FsType;
  cellId: string;
  /** 셀이 자기 dlView 스코프에서 해석한 identity. 편집 키잉의 1차 기준. */
  identityKey?: string;
  /** 편집이 발생한 실제 보고서 셀 위치 UID */
  locationUid?: string;
  newValue: string;
}

export interface IxdPasteFailure {
  cellId: string;
  uniqueId: string;
  errorMessage: string;
}

export interface PreparedIxdPasteValues {
  batch: CellEditBatchEntry[];
  changedEntries: TableCellPasteEntry[];
  failures: IxdPasteFailure[];
}

interface UseIxdCellEditHandlersOptions {
  editable: boolean;
  /** 반환값은 반영되지 않은 entries 인덱스 (연동 셀 충돌 등) */
  onCellEditBatch?: (entries: CellEditBatchEntry[]) => number[] | void;
}

const resolveFsTypeFromTd = (td: Element | null | undefined): FsType | null => {
  const wrapper = td?.closest<HTMLElement>("[data-ixd-fs-type]");
  const raw = wrapper?.getAttribute("data-ixd-fs-type");
  return raw === "consolidated" || raw === "separate" ? raw : null;
};

export const prepareIxdPasteValues = (
  entries: TableCellPasteEntry[],
): PreparedIxdPasteValues => {
  const batch: CellEditBatchEntry[] = [];
  const changedEntries: TableCellPasteEntry[] = [];
  const failures: IxdPasteFailure[] = [];

  for (const entry of entries) {
    const { cellId, uniqueId, element: td, value } = entry;
    const schemaType = td.getAttribute("data-schema-type") || "";
    const kind = getIxdSchemaValueKind(schemaType);
    const isMonetary = kind === "MONETARY";
    const currentRawValue = td.getAttribute("data-raw-value") ?? "";
    const isNegated = td.hasAttribute("data-negated");
    const decimalDigitsAttr = td.getAttribute("data-decimal-digits");
    const decimalDigits =
      decimalDigitsAttr !== null && decimalDigitsAttr !== ""
        ? Number(decimalDigitsAttr)
        : null;
    const currentValue = validateStoredCellValue({
      schemaType,
      storedValue: currentRawValue,
    });

    let newValue: string;
    if (value.trim() === "") {
      if (currentRawValue.trim() === "") continue;
      newValue = "";
    } else {
      const unitDivisor = Number(td.getAttribute("data-unit-divisor")) || 1;
      const normalized = normalizeEditedCellValue({
        schemaType,
        inputValue: value,
        unitDivisor,
        displayValueMode: isMonetary ? "unit-applied" : "raw",
        // 붙여넣는 값은 DSD 표시값이라 negated 표시 부호를 담고 있다 — 숫자 kind 전체 공통
        displayUsesNegatedSign: isNumericKind(kind),
        negated: isNegated,
        decimalDigits,
      });

      if (!normalized.ok) {
        console.error("[DEBUG] IXD 붙여넣기 값 검증 실패", {
          cellId,
          pastedValue: value,
          schemaType,
          unitDivisor,
          negated: isNegated,
          decimalDigits,
          errorMessage: normalized.errorMessage,
        });
        failures.push({
          cellId,
          uniqueId,
          errorMessage: normalized.errorMessage,
        });
        continue;
      }

      const currentComparableValue = currentValue.ok
        ? currentValue.normalizedValue
        : currentRawValue;
      if (currentComparableValue === normalized.storedValue) continue;
      newValue = normalized.storedValue;
    }

    if (td.hasAttribute("data-readonly")) {
      failures.push({
        cellId,
        uniqueId,
        errorMessage: "읽기 전용 셀에는 값을 붙여넣을 수 없습니다.",
      });
      continue;
    }

    const fsType = resolveFsTypeFromTd(td);
    if (!fsType) {
      failures.push({
        cellId,
        uniqueId,
        errorMessage: "셀의 재무제표 유형을 확인할 수 없습니다.",
      });
      continue;
    }

    batch.push({
      fsType,
      cellId,
      identityKey: td.getAttribute(ATTR.cellIdentityKey) ?? undefined,
      locationUid:
        td.getAttribute("data-cell-identity-location-uid") ?? undefined,
      newValue,
    });
    changedEntries.push(entry);
  }

  return { batch, changedEntries, failures };
};

/** 이미 저장 형식으로 확정된 값을 넣을 셀 (값 동기화 plan의 entry 등) */
export interface IxdStoredValueEntry {
  cellId: string;
  uniqueId: string;
  element: HTMLTableCellElement;
  /** 저장 형식 그대로의 값. 표시값이 아니다 */
  storedValue: string;
}

export interface PreparedIxdStoredValues {
  batch: CellEditBatchEntry[];
  changedEntries: IxdStoredValueEntry[];
  failures: IxdPasteFailure[];
}

/**
 * prepareIxdPasteValues의 형제 — 입력이 표시값이 아니라 이미 저장값일 때 쓴다.
 *
 * 표시값 경로는 쉼표·음수 표기·단위를 되돌려야 하지만(normalizeEditedCellValue),
 * 서버가 확정해 내려준 값은 그 왕복이 필요 없고 하면 오히려 위험하다 — 단위를
 * 한 번 더 역산하면 1000배가 된다. 그래서 정규화를 건너뛰되 readonly·fsType 등
 * 나머지 계약 검사는 그대로 태워 두 경로의 실패 처리를 같게 유지한다.
 */
export const prepareIxdStoredValues = (
  entries: IxdStoredValueEntry[],
): PreparedIxdStoredValues => {
  const batch: CellEditBatchEntry[] = [];
  const changedEntries: IxdStoredValueEntry[] = [];
  const failures: IxdPasteFailure[] = [];

  for (const entry of entries) {
    const { cellId, uniqueId, element: td, storedValue } = entry;
    const schemaType = td.getAttribute("data-schema-type") || "";
    const currentRawValue = td.getAttribute("data-raw-value") ?? "";

    // 양쪽을 같은 규칙으로 정규화해 비교한다. 표기만 다른 같은 값을 변경으로
    // 잡으면 매번 반영할 게 있는 것처럼 보인다.
    const comparable = (value: string): string => {
      const validated = validateStoredCellValue({
        schemaType,
        storedValue: value,
      });
      return validated.ok ? validated.normalizedValue : value;
    };
    if (comparable(currentRawValue) === comparable(storedValue)) continue;

    if (td.hasAttribute("data-readonly")) {
      failures.push({
        cellId,
        uniqueId,
        errorMessage: "읽기 전용 셀에는 값을 넣을 수 없습니다.",
      });
      continue;
    }

    const fsType = resolveFsTypeFromTd(td);
    if (!fsType) {
      failures.push({
        cellId,
        uniqueId,
        errorMessage: "셀의 재무제표 유형을 확인할 수 없습니다.",
      });
      continue;
    }

    batch.push({
      fsType,
      cellId,
      identityKey: td.getAttribute(ATTR.cellIdentityKey) ?? undefined,
      locationUid:
        td.getAttribute("data-cell-identity-location-uid") ?? undefined,
      newValue: storedValue,
    });
    changedEntries.push(entry);
  }

  return { batch, changedEntries, failures };
};

export function useIxdCellEditHandlers(
  containerRef: React.RefObject<HTMLDivElement | null>,
  { editable, onCellEditBatch }: UseIxdCellEditHandlersOptions,
) {
  // Enter 키로 선택 셀 편집 진입: 해당 td에 dblclick 이벤트 디스패치
  const handleEnterEdit = useCallback(
    (cellId: string, uniqueId?: string) => {
      if (!editable || !containerRef.current) return;
      const cell = uniqueId
        ? containerRef.current.querySelector(`#${CSS.escape(uniqueId)}`)
        : containerRef.current.querySelector(exactCellIdSelector(cellId));
      if (cell) {
        cell.dispatchEvent(new MouseEvent("dblclick", { bubbles: true }));
      }
    },
    [editable, containerRef],
  );

  // 붙여넣기: 표기값 기준 비교 후 갱신 (벌크 → undo 한 그룹)
  // 대상 td는 entry.element를 그대로 쓴다. data-cell-id는 연동 셀(다른 표의 동일 값)과
  // 겹칠 수 있어 querySelector 재조회 시 다른 단위 표의 td dataset을 읽게 된다.
  const handlePasteValues = useCallback(
    (entries: TableCellPasteEntry[]): TableCellPasteResult => {
      if (!editable) return { applied: 0, failed: entries.length };
      const { batch, failures } = prepareIxdPasteValues(entries);

      if (failures.length > 0) {
        const firstErrorMessage = failures[0]?.errorMessage;
        toast.error(
          failures.length === 1
            ? (firstErrorMessage ?? "붙여넣기 값 형식이 올바르지 않습니다.")
            : `${firstErrorMessage ?? "붙여넣기 값 형식이 올바르지 않습니다."} ${failures.length - 1}건이 추가로 제외되었습니다.`,
        );
      }

      if (batch.length === 0 || !onCellEditBatch) {
        return { applied: 0, failed: failures.length + batch.length };
      }
      const rejectedCount = onCellEditBatch(batch)?.length ?? 0;
      return {
        applied: batch.length - rejectedCount,
        failed: failures.length + rejectedCount,
      };
    },
    [editable, onCellEditBatch],
  );

  // 삭제: 선택 셀 값을 "" (빈 값 → sparse 삭제, readonly 셀 제외)
  const handleDeleteValues = useCallback(
    (cells: TableCellSelectionEntry[]) => {
      if (!editable) return;
      const batch: CellEditBatchEntry[] = [];
      for (const { cellId, element: td } of cells) {
        if (td.hasAttribute("data-readonly")) continue;
        const rawValue = td.getAttribute("data-raw-value") ?? "";
        if (rawValue.trim() === "") continue;
        const fsType = resolveFsTypeFromTd(td);
        if (!fsType) continue;
        // identity 는 렌더 시 그 셀의 dlView 스코프에서 해석돼 td 에 박혀 있다.
        // cellId 로 위층에서 역조회하면 축만 다른 표의 fact 를 집는다.
        const identityKey = td.getAttribute(ATTR.cellIdentityKey) ?? undefined;
        batch.push({
          fsType,
          cellId,
          identityKey,
          locationUid:
            td.getAttribute("data-cell-identity-location-uid") ?? undefined,
          newValue: "",
        });
      }
      if (batch.length > 0) {
        onCellEditBatch?.(batch);
      }
    },
    [editable, onCellEditBatch],
  );

  return { handleEnterEdit, handlePasteValues, handleDeleteValues };
}
